// AILiveShow.js
import React, { useState, useEffect, useRef } from 'react';
import './AILiveShow.css';
import axios from 'axios';

const AILiveShow = () => {
  const [aiComments, setAiComments] = useState([]);
  const [lastUpdated, setLastUpdated] = useState('');
  const [command, setCommand] = useState(null); // banner | comment | final
  const [isBannerFullscreen, setIsBannerFullscreen] = useState(false);
  const [currentActive, setCurrentActive] = useState(null); // 0 / 1
  const [hasData, setHasData] = useState(false);
  const lastCommentRef = useRef(null);
  const FINAL_PROMPT_TEXT = `
<div class="final-prompt" style="margin-left: 20px;">
  <h3>Closing Question for the Panel</h3>

  <p class="intro">
    Generate <strong>ONE</strong> closing question for the panel. This question should:
  </p>

  <ol>
    <li>Synthesize a theme, assumption, or blind spot that ran through the <strong>entire conversation</strong></li>
    <li>Surface something none of the panelists explicitly addressed, but their answers point toward</li>
    <li>Feel slightly uncomfortable — the kind that makes experienced leaders pause</li>
    <li>Be forward-looking, preparing the audience for the next <strong>2–3 years</strong></li>
    <li>Be a single question, clearly worded, no more than <strong>two sentences</strong></li>
  </ol>

  <div class="constraints">
    <h4>Constraints</h4>
    <ul>
      <li>Must be answerable by data center & connectivity infrastructure operators</li>
      <li>Stay within DC operations, power, connectivity, cloud & hyperscaler dynamics, AI/GPU demand</li>
      <li>Focus on Indonesian market, regulation, regional competition, or consolidation</li>
      <li>No philosophy, geopolitics, or macroeconomics</li>
      <li>Answerable from direct operational experience</li>
    </ul>
  </div>

  <div class="output-note">
    Output <strong>ONLY</strong> the question. No explanation.
  </div>
</div>
`;


  const formatCommentText = (text) => {
    return { __html: text || '' };
  };

  const getCommentTypeIcon = (type) => {
    switch(type) {
      case 'analysis': return '📊';
      case 'insight': return '💡';
      case 'perspective': return '👁️';
      default: return '💬';
    }
  };

  useEffect(() => {
    const fetchCommand = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_PORTAL_DESTINATION}getAILiveCommand`
        );
        console.log('getAILiveCommand response', res.data.data.command);

        if (res.data.data?.command) {
          setCommand(res.data.data.command);
        }
      } catch (err) {
        console.error('getAILiveCommand error', err);
      }
    };

    fetchCommand();
    const interval = setInterval(fetchCommand, 1000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (command === 'FinalPrompt') return;
        const res = await axios.get('https://portal.befa.id/getAILiveDB');
        const commentData = res.data?.data;

        if (!commentData) {
          setHasData(false);
          setCurrentActive(null);
          return;
        }

        // 🔒 CEK APAKAH DATA SAMA
        if (
          lastCommentRef.current &&
          lastCommentRef.current.comment === commentData.comment &&
          lastCommentRef.current.active === commentData.active
        ) {
          // ❗ data sama → jangan update state
          return;
        }

        lastCommentRef.current = commentData;

        const now = new Date();
        const timestamp = now.toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        });

        const newComment = {
          id: Date.now(),
          text: commentData.comment,
          timestamp,
          type: ['analysis', 'insight', 'perspective'][Math.floor(Math.random() * 3)]
        };

        setHasData(true);
        setAiComments([newComment]);
        setCurrentActive(commentData.active);

      } catch (error) {
        console.error('Error fetching AI comment:', error);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 5000);

    return () => clearInterval(interval);
  }, [command]);

  useEffect(() => {
  if (!hasData && command !== 'FinalPrompt') {
    setIsBannerFullscreen(true);
  }
}, [hasData, command]);

  useEffect(() => {
  if (!command) return;

  // === FINAL PROMPT (HARUS PALING ATAS) ===
  if (command === 'FinalPrompt') {
    const now = new Date();
    const timestamp = now.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });

    setHasData(true);
    setCurrentActive(1);
    setAiComments([{
      id: 'final-prompt',
      text: FINAL_PROMPT_TEXT,
      timestamp,
      type: 'final'
    }]);

    setIsBannerFullscreen(false);
    return;
  }

  // 🚨 NO DATA = FULLSCREEN
  if (!hasData || aiComments.length === 0) {
    setIsBannerFullscreen(true);
    return;
  }

  // === FORCE BANNER FULLSCREEN ===
  if (command === 'Banner') {
    setIsBannerFullscreen(true);
    return;
  }

  // === COMMENT / FINAL ===
  if (command === 'Comment' || command === 'Final') {
    setIsBannerFullscreen(currentActive === 1 ? false : true);
  }

}, [command, currentActive, hasData, aiComments.length]);


  return (
    <div className={`ai-live-show ${isBannerFullscreen ? 'banner-full' : ''}`}>
      {/* Header - FIXED HEIGHT */}
      <div className="show-header">
        <div className="event-banner">
          <img 
            src="/dist/img/Banner-AI-Liveshow.jpeg" 
            alt="Executive Panel Banner" 
            className="banner-image"
          />
          {/* <div className="banner-overlay">
            <h1>EXECUTIVE PANEL DISCUSSION</h1>
            <div className="event-theme">
              "Locking Anchors, Scaling Assets, Delivering Value."
            </div>
          </div> */}
        </div>
      </div>

      {/* Main Content - FILLS REMAINING SPACE */}
      {!isBannerFullscreen && aiComments.length > 0 && (
        <div className="show-main">
          <div className="comments-section">
            <div className="comments-header">
              <div className="comments-title">
                <h2>LIVE AI ANALYSIS</h2>
                <div className="live-status">
                  <span className="pulse-dot"></span>
                  LIVE
                </div>
              </div>
            </div>
            
            <div className="comments-display">
              {aiComments.length > 0 && (
                <div className="comment-display-container">
                  {aiComments.map((comment) => (
                    <div key={comment.id} className={`comment-card ${comment.type === 'final' ? 'final-mode' : ''}`}>
                      <div className="comment-meta">
                        <div className="meta-left">
                          <span className="comment-type">
                            {getCommentTypeIcon(comment.type)} {comment.type.toUpperCase()}
                          </span>
                        </div>
                        <div className="meta-right">
                          <span className="comment-time">{comment.timestamp}</span>
                        </div>
                      </div>
                      
                      <div 
                        className={`comment-content ${comment.type === 'final' ? 'final-content' : ''}`}
                        dangerouslySetInnerHTML={formatCommentText(comment.text)}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Footer - FIXED HEIGHT */}
      <div className="show-footer">
        <div className="footer-content">
          <div className="footer-left">
            <div className="footer-logo">EXECUTIVE AI PANEL</div>
            <div className="footer-theme">
              "Locking Anchors, Scaling Assets, Delivering Value."
            </div>
          </div>
          
          <div className="footer-center">
            
          </div>
          
          <div className="footer-right">
            <div className="footer-meta">
              <span>PT Kawasan Lintas Biru Digital</span>
              <span>•</span>
              <span>Live AI Show</span>
              <span></span>
              <span></span>
            </div>
          </div>
        </div>
      </div>
    </div>
    
  );
};

export default AILiveShow;